import { useState } from "react";

import {
  RETAILER_SCREEN_PREPACK_AFFECTED,
  RETAILER_SCREEN_RECEIVED,
  RETAILER_SCREEN_RESOLUTION,
  RETAILER_SCREEN_REVIEW,
  RETAILER_SCREEN_SELECT_PRODUCTS,
} from "../usePrototypeVariants";

import { PrePackAffectedScreen } from "./PrePackAffectedScreen";
import type {
  PrePackAllItemsAffected,
  PrePackReportContext,
} from "./prePackPolicyData";
import { PrePackResolutionScreen } from "./PrePackResolutionScreen";
import { ReportReceivedScreen } from "./ReportReceivedScreen";
import { ResolutionScreen } from "./ResolutionScreen";
import { ReturnRequestReceivedScreen } from "./ReturnRequestReceivedScreen";
import { ReturnRequestReviewScreen } from "./ReturnRequestReviewScreen";
import { ReviewConfirmScreen } from "./ReviewConfirmScreen";
import {
  DEFAULT_RETURN_REQUEST_SELECTED_IDS,
  getSelectedReturnRequestProducts,
} from "./returnRequestData";
import { SelectProductsScreen } from "./SelectProductsScreen";

type RetailerReportIssueFlowProps = {
  screen: number;
  onScreenChange: (screen: number) => void;
  isPrePackPolicy?: boolean;
  isReturnRequest?: boolean;
  prePackAllItemsAffected?: PrePackAllItemsAffected;
  onPrePackAffectedContinue?: (context: PrePackReportContext) => void;
};

export const RetailerReportIssueFlow = ({
  screen,
  onScreenChange,
  isPrePackPolicy = false,
  isReturnRequest = false,
  prePackAllItemsAffected = "yes",
  onPrePackAffectedContinue,
}: RetailerReportIssueFlowProps) => {
  const [selectedProductIds, setSelectedProductIds] = useState(
    () => new Set(DEFAULT_RETURN_REQUEST_SELECTED_IDS)
  );
  const selectedProducts = getSelectedReturnRequestProducts(selectedProductIds);

  const handleRemoveSelectedProduct = (productId: string) => {
    const nextIds = new Set(selectedProductIds);
    nextIds.delete(productId);
    setSelectedProductIds(nextIds);
  };

  const content = (() => {
    if (isPrePackPolicy) {
      switch (screen) {
        case RETAILER_SCREEN_RESOLUTION:
          return (
            <PrePackResolutionScreen
              key={prePackAllItemsAffected}
              allItemsAffected={prePackAllItemsAffected}
              onBack={() => onScreenChange(RETAILER_SCREEN_PREPACK_AFFECTED)}
              onContinue={() => onScreenChange(RETAILER_SCREEN_REVIEW)}
            />
          );
        case RETAILER_SCREEN_REVIEW:
          return (
            <ReviewConfirmScreen
              onEditResolution={() =>
                onScreenChange(RETAILER_SCREEN_RESOLUTION)
              }
              onSubmit={() => onScreenChange(RETAILER_SCREEN_RECEIVED)}
            />
          );
        case RETAILER_SCREEN_RECEIVED:
          return <ReportReceivedScreen />;
        case RETAILER_SCREEN_PREPACK_AFFECTED:
        default:
          return (
            <PrePackAffectedScreen
              onContinue={(context) => {
                onPrePackAffectedContinue?.(context);
              }}
            />
          );
      }
    }

    if (isReturnRequest) {
      switch (screen) {
        case RETAILER_SCREEN_RESOLUTION:
          return (
            <ResolutionScreen
              progressFraction={0.5}
              onBack={() => onScreenChange(RETAILER_SCREEN_SELECT_PRODUCTS)}
              onContinue={() => onScreenChange(RETAILER_SCREEN_REVIEW)}
            />
          );
        case RETAILER_SCREEN_REVIEW:
          return (
            <ReturnRequestReviewScreen
              products={selectedProducts}
              onRemoveProduct={handleRemoveSelectedProduct}
              onEditResolution={() =>
                onScreenChange(RETAILER_SCREEN_RESOLUTION)
              }
              onAddMore={() => onScreenChange(RETAILER_SCREEN_SELECT_PRODUCTS)}
              onBack={() => onScreenChange(RETAILER_SCREEN_RESOLUTION)}
              onSubmit={() => onScreenChange(RETAILER_SCREEN_RECEIVED)}
            />
          );
        case RETAILER_SCREEN_RECEIVED:
          return (
            <ReturnRequestReceivedScreen
              products={selectedProducts}
              onDone={() => onScreenChange(RETAILER_SCREEN_SELECT_PRODUCTS)}
            />
          );
        case RETAILER_SCREEN_SELECT_PRODUCTS:
        default:
          return (
            <SelectProductsScreen
              selectedIds={selectedProductIds}
              onSelectedIdsChange={setSelectedProductIds}
              onContinue={() => onScreenChange(RETAILER_SCREEN_RESOLUTION)}
            />
          );
      }
    }

    switch (screen) {
      case RETAILER_SCREEN_REVIEW:
        return (
          <ReviewConfirmScreen
            onEditResolution={() => onScreenChange(RETAILER_SCREEN_RESOLUTION)}
            onSubmit={() => onScreenChange(RETAILER_SCREEN_RECEIVED)}
          />
        );
      case RETAILER_SCREEN_RECEIVED:
        return <ReportReceivedScreen />;
      case RETAILER_SCREEN_RESOLUTION:
      default:
        return (
          <ResolutionScreen
            onContinue={() => onScreenChange(RETAILER_SCREEN_REVIEW)}
          />
        );
    }
  })();

  return (
    <div className="bg-fs-surface-secondary flex min-h-screen justify-center">
      <div className="bg-fs-surface-primary flex min-h-screen w-full max-w-[375px] flex-col shadow-sm">
        {content}
      </div>
    </div>
  );
};
